// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer table component module for displaying and managing customer data in a tabular format.
 * Provides sorting, filtering, and interactive features for customer management.
 */
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import {
  CUSTOMER_PAGE_SIZE,
  CustomerFilter,
} from '@/customer/api/get-customers';
import { CustomerTableRow } from '@/customer/components/customer-table-row';
import { CustomerTableSkeleton } from '@/customer/components/customer-table-skeleton';
import {
  CustomerSortProperty,
  useCustomers,
} from '@/customer/hooks/use-customers';
import { useTableSort } from '@/hooks/use-table-sort';
import { hasIntegration } from '@/utilities/integration';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';

/**
 * Props for the CustomerTable component
 */
interface Props {
  /**
   * The current sort configuration for the table.
   * Determines which column is being sorted and in what direction.
   */
  sort: Sort<CustomerSortProperty>;

  /**
   * Filter, search, and page criteria sent to the remote API, which also receives the sort.
   */
  filter?: CustomerFilter;

  /**
   * Callback fired when a column header is clicked for sorting.
   * @param property - The column property to sort by
   */
  onSort: (property: CustomerSortProperty) => void;

  /**
   * A callback fired when the user navigates to a different page.
   */
  onPageChange: (page: number) => void;
}

/**
 * A responsive table component that displays customer information with sorting and filtering capabilities.
 *
 * Features:
 * - Sortable columns for name, locations, and asset counts
 * - Responsive design with column hiding on smaller screens
 * - Empty state handling with helpful message
 * - Server-side pagination with a count and export footer
 * - Links to customer detail pages
 *
 * The component uses React Suspense for data loading and should be wrapped in a Suspense boundary.
 *
 * @example
 * ```tsx
 * <CustomerTable
 *   sort={{ property: 'name', direction: 'asc' }}
 *   filter={{ isArchived: false, page: 1, pageSize: 50 }}
 *   onSort={handleSort}
 *   onPageChange={handlePageChange}
 * />
 * ```
 */
export function CustomerTable(props: Props) {
  const { customers, resultCount } = useCustomers({
    filter: { ...props.filter, sort: props.sort },
  });

  const pageNumber = props.filter?.page ?? 1;
  const pageSize = props.filter?.pageSize ?? CUSTOMER_PAGE_SIZE;

  const onPageChange = props.onPageChange;

  // Get sort utilities for managing column sort states
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  // Configure the export URL for customer data
  const downloadURL = new URL(
    '/v1/export/customers',
    import.meta.env.VITE_API_URL
  );

  /**
   * Creates a sort handler for a specific column
   * @param column - The column property to sort by
   */
  const createSortHandler = (column: CustomerSortProperty) => () => {
    props.onSort(column);
  };

  const { user } = useCurrentUser();
  const hasCrmEnabled =
    hasIntegration(user.organization.enabledIntegrations, 'HUBSPOT') ||
    hasIntegration(user.organization.enabledIntegrations, 'SFDC');

  // Reset to page 1 when the current page returns no results (page overrun)
  useEffect(() => {
    if (customers.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [customers.length, pageNumber, onPageChange]);

  // A page past the last is on its way back to page one, so it is not an empty list.
  if (customers.length === 0 && pageNumber > 1) {
    return <CustomerTableSkeleton />;
  }

  // Display empty state when no companies are found
  if (customers.length === 0) {
    return (
      <EmptyState title="No companies found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking companies in Hardfin, add a new company
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              {/* Integration column */}
              {hasCrmEnabled && (
                <TableCell
                  sx={{
                    width: '3rem',
                    px: 2,
                    whiteSpace: 'nowrap',
                  }}
                />
              )}
              {/* Icon column */}
              <TableCell width="3rem" />

              {/* Name column - always visible */}
              <TableCell sortDirection={getSortDirection('name')}>
                <TableSortLabel
                  active={isLabelActive('name')}
                  direction={getLabelDirection('name')}
                  onClick={createSortHandler('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>

              {/* Role column - always visible */}
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                Role
              </TableCell>

              {/* Locations column - hidden on mobile */}
              <TableCell
                sortDirection={getSortDirection('locations')}
                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
              >
                <TableSortLabel
                  active={isLabelActive('locations')}
                  direction={getLabelDirection('locations')}
                  onClick={createSortHandler('locations')}
                >
                  Locations
                </TableSortLabel>
              </TableCell>

              {/* Assets on location column - hidden on mobile */}
              <TableCell
                sortDirection={getSortDirection('assets-on-location')}
                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
              >
                <TableSortLabel
                  active={isLabelActive('assets-on-location')}
                  direction={getLabelDirection('assets-on-location')}
                  onClick={createSortHandler('assets-on-location')}
                >
                  Assets on location
                </TableSortLabel>
              </TableCell>

              {/* Assets owned column - hidden on mobile */}
              <TableCell
                sortDirection={getSortDirection('assets-owned')}
                sx={{ display: { xs: 'none', sm: 'table-cell' } }}
              >
                <TableSortLabel
                  active={isLabelActive('assets-owned')}
                  direction={getLabelDirection('assets-owned')}
                  onClick={createSortHandler('assets-owned')}
                >
                  Assets owned
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <CustomerTableRow
                hasIntegration={hasCrmEnabled}
                key={customer.id}
                customer={customer}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange,
        }}
        downloadLabel="Export company data"
        downloadURL={downloadURL}
        totalCount={resultCount}
        entityOptions={{ singular: 'company', plural: 'companies' }}
      />
    </>
  );
}
